import type { EventTemplateBindingItem, Template } from '@/api/templates';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, LoadingState } from '@/components/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { EventBindingEventPanel } from './EventBindingEventPanel';
import { getEventBindingCoverage } from './bindingStatus';

interface EventBindingsCardProps {
  tenantIdForApi?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  /** 08.10.26 — внутри переключателя разделов шапка «Показать» не нужна. Не удалять. */
  embedded?: boolean;
  eventBindings: EventTemplateBindingItem[];
  eventBindingsLoading: boolean;
  eventBindingSavingByType: Record<string, boolean>;
  getBindingKey: (eventType: string, channel?: string) => string;
  getBindingOptions: (channel?: string) => Template[];
  onSetEventBinding: (eventType: string, templateId: string, channel?: string) => void;
  onClearEventBinding: (eventType: string, channel?: string) => void;
  onOpenTemplate: (templateId: string) => void;
}

export function EventBindingsCard({
  tenantIdForApi,
  isCollapsed,
  onToggleCollapse,
  eventBindings,
  eventBindingsLoading,
  eventBindingSavingByType,
  getBindingKey,
  getBindingOptions,
  onSetEventBinding,
  onClearEventBinding,
  onOpenTemplate,
  embedded = false,
}: EventBindingsCardProps) {
  const problematicCount = eventBindings.filter(
    (binding) => getEventBindingCoverage(binding).isProblematic,
  ).length;

  const open = embedded || !isCollapsed;

  return (
    <Card className="border-shell-border bg-shell-surface shadow-none">
      {!embedded && (
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-base font-extrabold tracking-tight text-shell-text">
            Привязки Event-Driven
          </CardTitle>
          <p className="max-w-3xl text-sm text-shell-muted">
            Generic и channel overrides для event pipeline. Проблемные события раскрыты по умолчанию.
          </p>
          {!isCollapsed && eventBindings.length > 0 ? (
            <p className="text-xs text-shell-muted">
              {eventBindings.length} типов · {problematicCount} требуют внимания
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onToggleCollapse} className="shrink-0 font-semibold">
          {isCollapsed ? (
            <ChevronRight className="mr-1 h-4 w-4" />
          ) : (
            <ChevronDown className="mr-1 h-4 w-4" />
          )}
          {isCollapsed ? 'Показать' : 'Скрыть'}
        </Button>
      </CardHeader>
      )}
      {open && (
        <CardContent className="pt-0">
          {!tenantIdForApi ? (
            <EmptyState
              type="search"
              title="Выберите клинику"
              description="Для настройки event-driven привязок выберите клинику."
            />
          ) : eventBindingsLoading ? (
            <LoadingState />
          ) : eventBindings.length === 0 ? (
            <EmptyState
              type="search"
              title="Нет event types"
              description="Для выбранной клиники привязки ещё не пришли с сервера."
            />
          ) : (
            <div className="space-y-2">
              {eventBindings.map((binding) => {
                const coverage = getEventBindingCoverage(binding);
                return (
                  <EventBindingEventPanel
                    key={binding.event_type}
                    binding={binding}
                    defaultOpen={coverage.isProblematic}
                    eventBindingSavingByType={eventBindingSavingByType}
                    getBindingKey={getBindingKey}
                    getBindingOptions={getBindingOptions}
                    onSetEventBinding={onSetEventBinding}
                    onClearEventBinding={onClearEventBinding}
                    onOpenTemplate={onOpenTemplate}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
